import { IEntity, injectTypes, ITicket, IWiwContext, WiwService } from 'Api/Plugins/Wiw/WiwService';
import { LocalizerService } from 'Api/Services';
import VoDatePicker from 'App/Areas/Shares/FormInputs/VoDatePicker.vue';
import { lazyInject } from 'App/Inversify.config';
import { BaseDialogBehavior } from 'App/Services/DialogService/BaseDialogBehavior';
import { Component } from 'Framework/Components/UIElement';
import { chunk } from 'lodash';
import { DataTableHeader } from 'vuetify';

interface IEplumeState {
    wiwId: string;
    ewattchId: string;
}

@Component({
    name: 'EplumeDialog',
    components: {
        VoDatePicker
    }
})
export default class EplumeDialog extends BaseDialogBehavior<IEplumeState> {
    public getTicketUri(ticket: ITicket): string {
        return `${this._wiwContext.baseUriWiw}/app/wiw.html?#!/ticket/${ticket.id}/details`
    }

    public getEntityUri(entity: IEntity): string {
        return `${this._wiwContext.baseUriWiw}/app/wiw.html?#!/product/${entity.id}/details`
    }

    public formatDate(date: Date): string {
        return date
            ? this._localizerService.localizeDate(date).format('LL')
            : '';
    }

    public async onSubmitAddTicketAsync(): Promise<void> {
        try {
            this.isBusy = true;

            let selectedStartDate: Date = this.formTicketStartDate
                ? new Date(this.formTicketStartDate)
                : null;

            let selectedEndDate: Date = this.formTicketEndDate
                ? new Date(this.formTicketEndDate)
                : null;

            const isSuccess = await this._wiwService.addTicketForEntityAsync(
                this.wiwEntity.id,
                this.formTicketName,
                this.formTicketDescription,
                selectedStartDate,
                selectedEndDate
            );

            if (isSuccess) {
                this.wiwEntity.tickets = await this._wiwService.getTicketsOfEntityAsync(this.wiwEntity.id);
            }
        }
        finally {
            this.isBusy = false;
            this.menu.isTicketModalOpen = false;
        }
    }

    public async onSubmitAddCommentAsync(): Promise<void> {
        try {
            this.isBusy = true;

            const isSuccess = await this._wiwService.addCommentForEntityAsync(
                this.wiwEntity.id,
                this.formCommentContent
            );

            if (isSuccess) {
                this.wiwEntity.comments = await this._wiwService.getCommentsOfEntityAsync(this.wiwEntity.id);
            }
        }
        finally {
            this.isBusy = false;
            this.menu.isCommentModalOpen = false;
        }
    }

    protected async _onDataContextChanged(newValue: IEplumeState, oldValue: IEplumeState): Promise<void> {
        let oldWiwId = oldValue ? oldValue.wiwId : null;
        let newWiwId = newValue ? newValue.wiwId : null;

        let oldEwattchId = oldValue ? oldValue.ewattchId : null;
        let newEwattchId = newValue ? newValue.ewattchId : null;

        if (newEwattchId && newEwattchId != oldEwattchId) {
            this.dashletEwattch = this._getEwattchData(newEwattchId);
        }

        if (newWiwId && newWiwId != oldWiwId) {
            if (!this._wiwService.isLogged) {
                await this._wiwService.loginTokenAsync(this._wiwContext.apiKey);
            }

            this.wiwEntity = await this._wiwService.getEntityAsync(newWiwId);
            await this._updateEntityAsync(this.wiwEntity);
        }
    }

    private async _updateEntityAsync(data: IEntity): Promise<void> {
        this.linkedImages = new Array<string>();
        const chunkedImages: Array<Array<string>> = chunk(data.images, 4);

        for (const images of chunkedImages) {
            const loadingImages = images.map(async (rawId: string) => {
                const id = rawId.substring(1);
                const blobImage = await this._wiwService.getBlobImageAsync(id);

                return URL.createObjectURL(blobImage);
            });

            const results = await Promise.allSettled(loadingImages)
            const imagesLoaded = results
                .filter(p => p.status == 'fulfilled')
                .map((p: PromiseFulfilledResult<string>) => p.value);

            this.linkedImages.push(...imagesLoaded);
        }
    }

    private _getEwattchData(ewattchId: string): string {
        if (ewattchId) {
            this.hasEwattchData = true;
            return `https://www.ewattchcloud.fr/dashlets/public/${ewattchId}`;
        }
        else {
            this.hasEwattchData = false;
            return null;
        }
    }

    public isBusy: boolean = false;
    public menu = {
        isTicketModalOpen: false,
        isCommentModalOpen: false
    };

    public headersTicket: Array<DataTableHeader> = [
        { text: 'Id', value: 'id' },
        { text: 'Nom', value: 'name' },
        { text: 'Date de création', value: 'created' },
        { text: 'Début prévu', value: 'scheduledBegin' },
        { text: 'Fin prévue', value: 'scheduledEnd' },
    ];
    public headersComments: Array<DataTableHeader> = [
        { text: 'Auteur', value: 'author' },
        { text: 'Date de création', value: 'date' }
    ];


    public hasEwattchData: boolean = false;
    public dashletEwattch: string = '';

    public wiwEntity: IEntity = null;
    public linkedImages: Array<string> = [];

    public formTicketName: string = '';
    public formTicketDescription: string = '';
    public formTicketStartDate: Date = null;
    public formTicketEndDate: Date = null;

    public formCommentContent: string = '';

    @lazyInject(WiwService)
    private readonly _wiwService: WiwService;

    @lazyInject(injectTypes.IWiwContext)
    private readonly _wiwContext: IWiwContext;

    @lazyInject(LocalizerService)
    private readonly _localizerService: LocalizerService;
}
